import { useQuery, useMutation } from '@tanstack/react-query';
import { FollowStatus } from '@shared/utils/db/followers/followers_base';
import { queryClient } from '@/pages/_app';
import { fetchFollowers, fetchFollowing, fetchPendingFollowRequests, followUser, unfollowUser, updateFollowStatus, checkFollowStatus } from '@/lib/db/DBFollowerHelpers';
interface UseFollowersQueryParams {
  username: string | null;
  status?: FollowStatus;
}
export const useFollowersQuery = ({
  username,
  status
}: UseFollowersQueryParams) => {
  return useQuery({
    queryKey: ['followers', {
      username,
      status
    }],
    queryFn: async () => {
      if (!username) return null;
      const followers = await fetchFollowers(username, status);
      return followers.data || null;
    },
    enabled: !!username,
    refetchInterval: 1000 * 30,
    refetchOnWindowFocus: false
  });
};
interface UseFollowingQueryParams {
  username: string | null;
  status?: FollowStatus;
  getFeed?: boolean;
}
export const useFollowingQuery = ({
  username,
  status,
  getFeed = false
}: UseFollowingQueryParams) => {
  return useQuery({
    queryKey: ['following', {
      username,
      status,
      getFeed
    }],
    queryFn: async () => {
      if (!username) return null;
      const following = await fetchFollowing(username, status, getFeed);
      return following.data || null;
    },
    enabled: !!username,
    refetchInterval: 1000 * 30,
    refetchOnWindowFocus: false
  });
};
interface UseFollowStatusQueryParams {
  username: string | null;
  targetUsername: string | null;
}
export const useFollowStatusQuery = ({
  username,
  targetUsername
}: UseFollowStatusQueryParams) => {
  return useQuery({
    queryKey: ['followStatus', {
      username,
      targetUsername
    }],
    queryFn: async () => {
      if (!username || !targetUsername) return null;
      const status = await checkFollowStatus(username, targetUsername);
      return status.data || null;
    },
    enabled: !!username && !!targetUsername,
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false
  });
};
interface UseFollowersDataQueryParams {
  username: string | null;
}
export const useFollowersDataQuery = ({
  username
}: UseFollowersDataQueryParams) => {
  return useQuery({
    queryKey: ['followersData', {
      username
    }],
    queryFn: async () => {
      if (!username) return null;
      const [followers, following, pending] = await Promise.all([fetchFollowers(username), fetchFollowing(username), fetchPendingFollowRequests(username)]);
      return {
        followers: followers.data || [],
        following: following.data || [],
        pendingRequests: pending.data || []
      };
    },
    enabled: !!username,
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false
  });
};
interface FollowMutationParams {
  username: string;
  userToFollow: string;
}
export const useFollowMutation = () => {
  return useMutation({
    mutationFn: async ({
      username,
      userToFollow
    }: FollowMutationParams) => {
      const result = await followUser(username, userToFollow);
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['following', {
          username: variables.username
        }]
      });
      queryClient.invalidateQueries({
        queryKey: ['followStatus', {
          username: variables.username
        }]
      });
      queryClient.invalidateQueries({
        queryKey: ['followersData']
      });
    }
  });
};
interface UnfollowMutationParams {
  username: string;
  userToUnfollow: string;
}
export const useUnfollowMutation = () => {
  return useMutation({
    mutationFn: async ({
      username,
      userToUnfollow
    }: UnfollowMutationParams) => {
      const result = await unfollowUser(username, userToUnfollow);
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['followers']
      });
      queryClient.invalidateQueries({
        queryKey: ['following']
      });
      queryClient.invalidateQueries({
        queryKey: ['followStatus', {
          username: variables.username
        }]
      });
      queryClient.invalidateQueries({
        queryKey: ['followersData']
      });
    }
  });
};
interface UpdateFollowStatusMutationParams {
  followerUsername: string;
  newStatus: FollowStatus;
}
export const useUpdateFollowStatusMutation = () => {
  return useMutation({
    mutationFn: async ({
      followerUsername,
      newStatus
    }: UpdateFollowStatusMutationParams) => {
      const result = await updateFollowStatus(followerUsername, newStatus);
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['followers']
      });
      queryClient.invalidateQueries({
        queryKey: ['following']
      });
      queryClient.invalidateQueries({
        queryKey: ['pendingFollowRequests']
      });
      queryClient.invalidateQueries({
        queryKey: ['followersData']
      });
    }
  });
};

// Usage example:
/*
const Component = () => {
  const { data: followersData } = useFollowersQuery({ username: "user1" });
  const { data: followingData } = useFollowingQuery({ username: "user1", status: "public" });
  const { data: followStatus } = useFollowStatusQuery({ 
    username: "user1", 
    targetUsername: "user2" 
  });
  
  const followMutation = useFollowMutation();
  
  const handleFollow = async () => {
    await followMutation.mutateAsync({ 
      username: "user1", 
      userToFollow: "user2" 
    });
  };
};
*/